import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fmtDateTime, fmtGHS, fmtNum, pName, pMatch } from "@/lib/format";
import { Pill, ScrollText, Plus, Trash2, CheckCircle2, XCircle, AlertTriangle, Pencil, Info } from "lucide-react";
import { toast } from "sonner";
import { ProductCombobox } from "@/components/ProductCombobox";

const today = () => new Date().toISOString().slice(0, 10);
const badge = (s) => (s === "approved" ? "ok" : s === "rejected" ? "out" : "pending");
const emptyDispLine = () => ({ product_id: "", units: "" });
const emptyIns = () => ({ scheme: "", value: "" });
const emptyCredit = () => ({ client: "", value: "" });
const SHIFTS = ["Morning", "Afternoon", "Night"];
const asLines = (v, key) => (Array.isArray(v) ? v : v?.value ? [{ [key]: "Unspecified", value: v.value }] : []);

const ShiftSelect = ({ value, onChange, testId, allowAll }) => (
  <Select value={value || (allowAll ? "all" : "")} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
    <SelectTrigger className="w-40 h-9" data-testid={testId}><SelectValue placeholder="Select shift" /></SelectTrigger>
    <SelectContent>
      {allowAll && <SelectItem value="all">All shifts</SelectItem>}
      {SHIFTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
    </SelectContent>
  </Select>
);

export default function WarehouseDaily() {
  const { can } = useAuth();
  const { warehouses } = useBranch();
  const canEdit = can("inventory.adjust");
  const canApprove = can("adjustment.approve");

  const [branchId, setBranchId] = useState("");
  const [date, setDate] = useState(today());
  const [shift, setShift] = useState("");
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [disp, setDisp] = useState([]);
  const [sales, setSales] = useState([]);
  const [variance, setVariance] = useState(null);

  useEffect(() => { if (!branchId && warehouses.length) setBranchId(warehouses[0].id); }, [warehouses, branchId]);
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/customers", { params: { type: "corporate" } }).then((r) => setClients(r.data)).catch(() => {});
    api.get("/insurance-schemes").then((r) => setSchemes(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!branchId || !date) return;
    const params = { branch_id: branchId, date, ...(shift ? { shift_label: shift } : {}) };
    api.get("/warehouse/dispensing", { params }).then((r) => setDisp(r.data)).catch(() => {});
    api.get("/warehouse/sales-summary", { params }).then((r) => setSales(r.data)).catch(() => {});
    api.get("/warehouse/variance", { params }).then((r) => setVariance(r.data)).catch(() => setVariance(null));
  }, [branchId, date, shift]);
  useEffect(() => { load(); }, [load]);

  // ── dispensing dialog ──
  const [dOpen, setDOpen] = useState(false);
  const [dEditId, setDEditId] = useState(null);
  const [dLines, setDLines] = useState([emptyDispLine()]);
  const [dNote, setDNote] = useState("");
  const [dShift, setDShift] = useState("");
  const openDispNew = () => { setDEditId(null); setDLines([emptyDispLine()]); setDNote(""); setDShift(shift); setDOpen(true); };
  const openDispEdit = (d) => { setDEditId(d.id); setDLines(d.lines.map((l) => ({ product_id: l.product_id, units: String(l.units) }))); setDNote(d.note || ""); setDShift(d.shift_label || ""); setDOpen(true); };
  const setDLine = (i, patch) => setDLines((a) => a.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const submitDisp = async () => {
    const lines = dLines.filter((l) => l.product_id && Number(l.units) > 0).map((l) => ({ product_id: l.product_id, units: Number(l.units) }));
    if (!lines.length) return toast.error("Add at least one product with units dispensed");
    if (!dShift) return toast.error("Select the shift for this entry");
    const payload = { branch_id: branchId, date, shift_label: dShift, note: dNote, lines };
    try {
      if (dEditId) { await api.put(`/warehouse/dispensing/${dEditId}`, payload); toast.success("Dispensing entry updated"); }
      else { const { data } = await api.post("/warehouse/dispensing", payload); toast.success(`${data.code} raised — awaiting approval`); }
      setDOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  // ── sales dialog ──
  const [sOpen, setSOpen] = useState(false);
  const [sEditId, setSEditId] = useState(null);
  const [cash, setCash] = useState({ value: "" });
  const [credit, setCredit] = useState([emptyCredit()]);
  const [insurance, setInsurance] = useState([emptyIns()]);
  const [unitsSold, setUnitsSold] = useState("");
  const [sNote, setSNote] = useState("");
  const [sShift, setSShift] = useState("");
  const openSalesNew = () => { setSEditId(null); setCash({ value: "" }); setCredit([emptyCredit()]); setInsurance([emptyIns()]); setUnitsSold(""); setSNote(""); setSShift(shift); setSOpen(true); };
  const openSalesEdit = (s) => {
    setSEditId(s.id);
    setSShift(s.shift_label || "");
    setCash({ value: String(s.cash?.value ?? "") });
    const cr0 = asLines(s.credit, "client"), ins0 = asLines(s.insurance, "scheme");
    setCredit(cr0.length ? cr0.map((c) => ({ client: c.client, value: String(c.value) })) : [emptyCredit()]);
    setInsurance(ins0.length ? ins0.map((i) => ({ scheme: i.scheme, value: String(i.value) })) : [emptyIns()]);
    setUnitsSold(String(s.units_sold ?? s.total_units ?? ""));
    setSNote(s.note || ""); setSOpen(true);
  };
  const setIns = (i, patch) => setInsurance((a) => a.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const setCr = (i, patch) => setCredit((a) => a.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const liveSalesValue = Number(cash.value || 0) + credit.reduce((a, c) => a + Number(c.value || 0), 0) + insurance.reduce((a, i) => a + Number(i.value || 0), 0);

  const submitSales = async () => {
    const cr = credit.filter((c) => (c.client || "").trim()).map((c) => ({ client: c.client.trim(), value: Number(c.value || 0) }));
    const ins = insurance.filter((i) => (i.scheme || "").trim()).map((i) => ({ scheme: i.scheme.trim(), value: Number(i.value || 0) }));
    if (!sShift) return toast.error("Select the shift for this summary");
    const payload = {
      branch_id: branchId, date, shift_label: sShift, note: sNote,
      cash: { value: Number(cash.value || 0) },
      credit: cr, insurance: ins, units_sold: Number(unitsSold || 0),
    };
    try {
      if (sEditId) { await api.put(`/warehouse/sales-summary/${sEditId}`, payload); toast.success("Sales summary updated"); }
      else { const { data } = await api.post("/warehouse/sales-summary", payload); toast.success(`${data.code} raised — awaiting approval`); }
      setSOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  // ── detail / approve ──
  const [detail, setDetail] = useState(null);   // {kind:'disp'|'sales', row}
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const approve = async () => {
    const { kind, row } = detail;
    const url = kind === "disp" ? `/warehouse/dispensing/${row.id}/approve` : `/warehouse/sales-summary/${row.id}/approve`;
    try { await api.post(url); toast.success(kind === "disp" ? `Approved — ${row.total_units} unit(s) deducted` : "Sales summary approved"); setDetail(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const doReject = async () => {
    const { kind, row } = rejectFor;
    const url = kind === "disp" ? `/warehouse/dispensing/${row.id}/reject` : `/warehouse/sales-summary/${row.id}/reject`;
    try { await api.post(url, { reason: rejectReason }); toast.success("Entry rejected"); setRejectFor(null); setRejectReason(""); setDetail(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const noWarehouse = warehouses.length === 0;

  return (
    <div>
      <PageHeader title="Warehouse Daily Entries" subtitle="Interim daily capture for the warehouse — prescription dispensing and sales summary." />

      <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 px-3 py-2 mb-4 text-sm text-amber-800 dark:text-amber-300" data-testid="interim-banner">
        <Info className="h-4 w-4 mt-0.5 shrink-0" />
        <span><strong>Interim feature.</strong> These manual daily entries bridge the gap before HMS integration and will be replaced once HMS is connected.</span>
      </div>

      {noWarehouse ? (
        <Card className="p-10 text-center text-muted-foreground">No warehouse location configured. Mark a branch as a warehouse to use daily entries.</Card>
      ) : (
        <>
          <div className="flex flex-wrap gap-3 items-end mb-4">
            <div><Label className="text-xs">Warehouse</Label>
              <Select value={branchId} onValueChange={setBranchId}>
                <SelectTrigger className="w-56" data-testid="wh-branch-select"><SelectValue placeholder="Select warehouse" /></SelectTrigger>
                <SelectContent>{warehouses.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Date</Label>
              <Input type="date" className="w-44" value={date} onChange={(e) => setDate(e.target.value)} data-testid="wh-date-input" />
            </div>
            <div><Label className="text-xs">Shift</Label>
              <ShiftSelect value={shift} onChange={setShift} testId="wh-shift-filter" allowAll />
            </div>
          </div>

          {/* Variance banner */}
          {variance && (variance.has_dispensing || variance.has_sales) && (
            <Card className={`p-3 mb-4 flex items-center gap-3 ${variance.match ? "border-emerald-300 bg-emerald-50 dark:bg-emerald-950/20" : "border-rose-300 bg-rose-50 dark:bg-rose-950/20"}`} data-testid="variance-banner">
              {variance.match ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <AlertTriangle className="h-5 w-5 text-rose-600" />}
              <div className="text-sm">
                <span className="font-medium">{variance.match ? "Units reconciled" : "Unit variance detected"}{shift ? ` · ${shift} shift` : " · whole day"}</span>
                <span className="text-muted-foreground ml-2">Dispensed: <strong>{fmtNum(variance.dispensing_units)}</strong> · Sales units: <strong>{fmtNum(variance.sales_units)}</strong>
                  {!variance.match && <span className="text-rose-600 ml-2">Difference: {variance.diff > 0 ? "+" : ""}{fmtNum(variance.diff)}</span>}</span>
              </div>
            </Card>
          )}

          <Tabs defaultValue="dispensing">
            <TabsList data-testid="wh-tabs">
              <TabsTrigger value="dispensing" data-testid="tab-dispensing"><Pill className="h-4 w-4 mr-1.5" /> Prescription Dispensing</TabsTrigger>
              <TabsTrigger value="sales" data-testid="tab-sales"><ScrollText className="h-4 w-4 mr-1.5" /> Sales Summary</TabsTrigger>
            </TabsList>

            {/* Dispensing tab */}
            <TabsContent value="dispensing" className="mt-3">
              <div className="flex justify-end mb-2">
                {canEdit && <Button size="sm" data-testid="new-dispensing-btn" onClick={openDispNew}><Plus className="h-4 w-4 mr-1" /> New dispensing entry</Button>}
              </div>
              <Card className="shadow-sm overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground text-left">
                    <tr><th className="py-2.5 px-3">Code</th><th className="py-2.5 px-3">Date</th><th className="py-2.5 px-3">Shift</th><th className="py-2.5 px-3 text-right">Products</th><th className="py-2.5 px-3 text-right">Total units</th><th className="py-2.5 px-3">Status</th><th className="py-2.5 px-3">Raised by</th><th className="py-2.5 px-3"></th></tr>
                  </thead>
                  <tbody>
                    {disp.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-muted-foreground"><Pill className="h-8 w-8 mx-auto mb-2 opacity-40" />No dispensing entries for this {shift ? "shift" : "day"}</td></tr>}
                    {disp.map((d) => (
                      <tr key={d.id} className="border-t border-border hover:bg-muted/50 cursor-pointer" data-testid={`disp-row-${d.code}`} onClick={() => setDetail({ kind: "disp", row: d })}>
                        <td className="py-2 px-3 font-mono text-xs">{d.code}</td>
                        <td className="py-2 px-3">{d.date}</td>
                        <td className="py-2 px-3" data-testid={`disp-shift-${d.code}`}>{d.shift_label || "—"}</td>
                        <td className="py-2 px-3 text-right font-mono">{d.lines.length}</td>
                        <td className="py-2 px-3 text-right font-mono">{fmtNum(d.total_units)}</td>
                        <td className="py-2 px-3"><StatusBadge value={badge(d.status)} label={d.status} /></td>
                        <td className="py-2 px-3 text-muted-foreground">{d.created_by}</td>
                        <td className="py-2 px-3 text-right">
                          {d.status === "pending" && canEdit && <Button size="sm" variant="ghost" data-testid={`edit-disp-${d.code}`} onClick={(e) => { e.stopPropagation(); openDispEdit(d); }}><Pencil className="h-3.5 w-3.5" /></Button>}
                          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setDetail({ kind: "disp", row: d }); }}>View</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </TabsContent>

            {/* Sales tab */}
            <TabsContent value="sales" className="mt-3">
              <div className="flex justify-end mb-2">
                {canEdit && <Button size="sm" data-testid="new-sales-btn" onClick={openSalesNew}><Plus className="h-4 w-4 mr-1" /> New sales summary</Button>}
              </div>
              <Card className="shadow-sm overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground text-left">
                    <tr><th className="py-2.5 px-3">Code</th><th className="py-2.5 px-3">Date</th><th className="py-2.5 px-3">Shift</th><th className="py-2.5 px-3 text-right">Total value</th><th className="py-2.5 px-3 text-right">Total units</th><th className="py-2.5 px-3">Status</th><th className="py-2.5 px-3">Raised by</th><th className="py-2.5 px-3"></th></tr>
                  </thead>
                  <tbody>
                    {sales.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-muted-foreground"><ScrollText className="h-8 w-8 mx-auto mb-2 opacity-40" />No sales summary for this {shift ? "shift" : "day"}</td></tr>}
                    {sales.map((s) => (
                      <tr key={s.id} className="border-t border-border hover:bg-muted/50 cursor-pointer" data-testid={`sales-row-${s.code}`} onClick={() => setDetail({ kind: "sales", row: s })}>
                        <td className="py-2 px-3 font-mono text-xs">{s.code}</td>
                        <td className="py-2 px-3">{s.date}</td>
                        <td className="py-2 px-3" data-testid={`sales-shift-${s.code}`}>{s.shift_label || "—"}</td>
                        <td className="py-2 px-3 text-right font-mono">{fmtGHS(s.total_value)}</td>
                        <td className="py-2 px-3 text-right font-mono">{fmtNum(s.total_units)}</td>
                        <td className="py-2 px-3"><StatusBadge value={badge(s.status)} label={s.status} /></td>
                        <td className="py-2 px-3 text-muted-foreground">{s.created_by}</td>
                        <td className="py-2 px-3 text-right">
                          {s.status === "pending" && canEdit && <Button size="sm" variant="ghost" data-testid={`edit-sales-${s.code}`} onClick={(e) => { e.stopPropagation(); openSalesEdit(s); }}><Pencil className="h-3.5 w-3.5" /></Button>}
                          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setDetail({ kind: "sales", row: s }); }}>View</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}

      {/* Dispensing create/edit */}
      <Dialog open={dOpen} onOpenChange={setDOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{dEditId ? "Edit dispensing entry" : "New prescription dispensing"}</DialogTitle>
            <DialogDescription>Enter total units dispensed per product for {date}. Stock is deducted (FEFO) only on approval.</DialogDescription></DialogHeader>
          <div><Label className="text-xs">Shift</Label><ShiftSelect value={dShift} onChange={setDShift} testId="disp-shift-select" /></div>
          <div className="space-y-2">
            {dLines.map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end border border-border rounded-lg p-2">
                <div className="col-span-8"><Label className="text-xs">Product</Label>
                  <ProductCombobox products={products} value={l.product_id} onChange={(v) => setDLine(i, { product_id: v })} testId={`disp-product-${i}`} />
                </div>
                <div className="col-span-3"><Label className="text-xs">Units dispensed</Label><Input type="number" min="1" className="h-9" data-testid={`disp-units-${i}`} value={l.units} onChange={(e) => setDLine(i, { units: e.target.value })} /></div>
                <div className="col-span-1 flex justify-end"><button className="text-muted-foreground hover:text-rose-600 pb-2" data-testid={`disp-remove-${i}`} onClick={() => setDLines((a) => a.filter((_, idx) => idx !== i))}><Trash2 className="h-4 w-4" /></button></div>
              </div>
            ))}
          </div>
          <Button size="sm" variant="outline" data-testid="disp-add-line" onClick={() => setDLines((a) => [...a, emptyDispLine()])}><Plus className="h-3.5 w-3.5 mr-1" /> Add product</Button>
          <div><Label className="text-xs">Note (optional)</Label><Input data-testid="disp-note-input" value={dNote} onChange={(e) => setDNote(e.target.value)} /></div>
          <DialogFooter><Button variant="outline" onClick={() => setDOpen(false)}>Cancel</Button><Button data-testid="submit-dispensing-btn" onClick={submitDisp}>{dEditId ? "Save changes" : "Submit for approval"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sales create/edit */}
      <Dialog open={sOpen} onOpenChange={setSOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{sEditId ? "Edit sales summary" : "New daily sales summary"}</DialogTitle>
            <DialogDescription>Total sales value and units dispensed for one shift on {date}, by payment type. Only one summary is allowed per shift.</DialogDescription></DialogHeader>
          <div><Label className="text-xs">Shift</Label><ShiftSelect value={sShift} onChange={setSShift} testId="sales-shift-select" /></div>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2 items-end border border-border rounded-lg p-2">
              <Label className="text-sm font-medium">Cash</Label>
              <div className="col-span-2"><Label className="text-xs">Value (GHS)</Label><Input type="number" className="h-9" data-testid="cash-value" value={cash.value} onChange={(e) => setCash({ value: e.target.value })} /></div>
            </div>

            <div className="border border-border rounded-lg p-2 space-y-2">
              <div className="flex items-center justify-between"><Label className="text-sm font-medium">Credit — corporate accounts</Label>
                <Button size="sm" variant="outline" data-testid="add-credit-line" onClick={() => setCredit((a) => [...a, emptyCredit()])}><Plus className="h-3.5 w-3.5 mr-1" /> Add client</Button></div>
              {credit.map((c, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-8"><Label className="text-xs">Corporate client</Label>
                    {clients.length ? (
                      <Select value={c.client} onValueChange={(v) => setCr(i, { client: v })}>
                        <SelectTrigger className="h-9" data-testid={`credit-client-${i}`}><SelectValue placeholder="Select client" /></SelectTrigger>
                        <SelectContent className="max-h-64">{clients.map((cl) => <SelectItem key={cl.id} value={cl.name}>{cl.name}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <Input className="h-9" placeholder="Corporate client" data-testid={`credit-client-${i}`} value={c.client} onChange={(e) => setCr(i, { client: e.target.value })} />
                    )}
                  </div>
                  <div className="col-span-3"><Label className="text-xs">Value (GHS)</Label><Input type="number" className="h-9" data-testid={`credit-value-${i}`} value={c.value} onChange={(e) => setCr(i, { value: e.target.value })} /></div>
                  <div className="col-span-1 flex justify-end"><button className="text-muted-foreground hover:text-rose-600 pb-2" data-testid={`credit-remove-${i}`} onClick={() => setCredit((a) => a.filter((_, idx) => idx !== i))}><Trash2 className="h-4 w-4" /></button></div>
                </div>
              ))}
            </div>

            <div className="border border-border rounded-lg p-2 space-y-2">
              <div className="flex items-center justify-between"><Label className="text-sm font-medium">Insurance schemes</Label>
                <Button size="sm" variant="outline" data-testid="add-insurance-line" onClick={() => setInsurance((a) => [...a, emptyIns()])}><Plus className="h-3.5 w-3.5 mr-1" /> Add scheme</Button></div>
              {insurance.map((ins, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-8"><Label className="text-xs">Scheme</Label>
                    {schemes.length ? (
                      <Select value={ins.scheme} onValueChange={(v) => setIns(i, { scheme: v })}>
                        <SelectTrigger className="h-9" data-testid={`ins-scheme-${i}`}><SelectValue placeholder="Select scheme" /></SelectTrigger>
                        <SelectContent className="max-h-64">{schemes.map((sc) => <SelectItem key={sc.id} value={sc.name}>{sc.name}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <Input className="h-9" placeholder="e.g. NHIS, Nationwide" data-testid={`ins-scheme-${i}`} value={ins.scheme} onChange={(e) => setIns(i, { scheme: e.target.value })} />
                    )}
                  </div>
                  <div className="col-span-3"><Label className="text-xs">Value (GHS)</Label><Input type="number" className="h-9" data-testid={`ins-value-${i}`} value={ins.value} onChange={(e) => setIns(i, { value: e.target.value })} /></div>
                  <div className="col-span-1 flex justify-end"><button className="text-muted-foreground hover:text-rose-600 pb-2" data-testid={`ins-remove-${i}`} onClick={() => setInsurance((a) => a.filter((_, idx) => idx !== i))}><Trash2 className="h-4 w-4" /></button></div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-3 items-end">
              <div><Label className="text-xs">Total units sold this shift <span className="text-muted-foreground">(for variance check)</span></Label><Input type="number" min="0" className="h-9" data-testid="sales-units-sold" value={unitsSold} onChange={(e) => setUnitsSold(e.target.value)} /></div>
              <div className="text-right text-sm text-muted-foreground pb-1">Total value: <strong className="text-foreground">{fmtGHS(liveSalesValue)}</strong></div>
            </div>
            <div><Label className="text-xs">Note (optional)</Label><Input data-testid="sales-note-input" value={sNote} onChange={(e) => setSNote(e.target.value)} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setSOpen(false)}>Cancel</Button><Button data-testid="submit-sales-btn" onClick={submitSales}>{sEditId ? "Save changes" : "Submit for approval"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail / approve */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-2">{detail.row.code} · {detail.row.date}{detail.row.shift_label ? ` · ${detail.row.shift_label} shift` : ""} <StatusBadge value={badge(detail.row.status)} label={detail.row.status} /></DialogTitle>
                <DialogDescription>Raised by {detail.row.created_by} · {fmtDateTime(detail.row.created_at)}{detail.row.note ? ` · ${detail.row.note}` : ""}</DialogDescription></DialogHeader>

              {detail.kind === "disp" ? (
                <div className="overflow-x-auto border-y border-border">
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase text-muted-foreground text-left"><tr><th className="py-2 px-2">Product</th><th className="py-2 px-2 text-right">Units</th><th className="py-2 px-2">Batches used</th></tr></thead>
                    <tbody>
                      {detail.row.lines.map((l, i) => (
                        <tr key={i} className="border-t border-border">
                          <td className="py-1.5 px-2">{l.generic_name}</td>
                          <td className="py-1.5 px-2 text-right font-mono">{l.units}</td>
                          <td className="py-1.5 px-2 text-xs text-muted-foreground">{(l.allocations || []).map((a) => `${a.batch_no} ×${a.quantity}`).join(", ") || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot><tr className="border-t border-border font-medium"><td className="py-1.5 px-2">Total</td><td className="py-1.5 px-2 text-right font-mono">{fmtNum(detail.row.total_units)}</td><td /></tr></tfoot>
                  </table>
                </div>
              ) : (
                <div className="overflow-x-auto border-y border-border">
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase text-muted-foreground text-left"><tr><th className="py-2 px-2">Payment type</th><th className="py-2 px-2 text-right">Value</th></tr></thead>
                    <tbody>
                      <tr className="border-t border-border"><td className="py-1.5 px-2">Cash</td><td className="py-1.5 px-2 text-right font-mono">{fmtGHS(detail.row.cash?.value)}</td></tr>
                      {asLines(detail.row.credit, "client").map((c, i) => (
                        <tr key={`c${i}`} className="border-t border-border"><td className="py-1.5 px-2">Credit · {c.client}</td><td className="py-1.5 px-2 text-right font-mono">{fmtGHS(c.value)}</td></tr>
                      ))}
                      {asLines(detail.row.insurance, "scheme").map((ins, i) => (
                        <tr key={`i${i}`} className="border-t border-border"><td className="py-1.5 px-2">Insurance · {ins.scheme}</td><td className="py-1.5 px-2 text-right font-mono">{fmtGHS(ins.value)}</td></tr>
                      ))}
                    </tbody>
                    <tfoot><tr className="border-t border-border font-medium"><td className="py-1.5 px-2">Total value</td><td className="py-1.5 px-2 text-right font-mono">{fmtGHS(detail.row.total_value)}</td></tr></tfoot>
                  </table>
                  <p className="text-sm text-muted-foreground px-2 py-2">Total units sold: <strong className="text-foreground">{fmtNum(detail.row.total_units)}</strong></p>
                </div>
              )}

              {detail.row.status === "rejected" && detail.row.reject_reason && <p className="text-sm text-rose-600">Rejected: {detail.row.reject_reason}</p>}
              {detail.row.status === "approved" && <p className="text-sm text-emerald-600">Approved by {detail.row.approved_by} · {fmtDateTime(detail.row.approved_at)}</p>}
              {detail.row.status === "pending" && canApprove && (
                <DialogFooter className="gap-2">
                  <Button variant="outline" className="text-rose-600 border-rose-300" data-testid="reject-entry-btn" onClick={() => setRejectFor(detail)}><XCircle className="h-4 w-4 mr-1.5" /> Reject</Button>
                  <Button data-testid="approve-entry-btn" onClick={approve}><CheckCircle2 className="h-4 w-4 mr-1.5" /> {detail.kind === "disp" ? "Approve & deduct stock" : "Approve"}</Button>
                </DialogFooter>
              )}
              {detail.row.status === "pending" && !canApprove && <p className="text-xs text-muted-foreground">Awaiting approval by a Super Admin or Accountant.</p>}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject reason */}
      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Reject {rejectFor?.row?.code}</DialogTitle></DialogHeader>
          <div><Label>Reason</Label><Input data-testid="entry-reject-reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} /></div>
          <DialogFooter><Button variant="outline" onClick={() => setRejectFor(null)}>Cancel</Button><Button className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-reject-entry-btn" onClick={doReject}>Confirm rejection</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
